function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import * as React from "react";
import clsx from "clsx";
import { makeStyles } from "@material-ui/styles";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import { useTheme } from "@material-ui/core";
import ParcelLogo from "../ParcelLogo";
import { useMapSize, useDeferredValue } from "../../hooks";
import FallBackImage from "../FallBackImage";
import TimeProgress from "./TimeProgress";
import { getMapImage } from "../../utils/images";
import { joinStringsWith } from "../../utils/string";
import { Identity, Plug } from "../Icons";
import { ID_MESSAGE } from "./constants";
import { useEnvContext } from "../../env";
import defaultMap from "../../images/tracking.jpg";
import { HEADER_HEIGHT } from "../../constants/values";
const useStyles = makeStyles(({
  palette,
  typography
}) => ({
  container: {
    padding: `${typography.pxToRem(20)} ${typography.pxToRem(200)} ${typography.pxToRem(10)} ${typography.pxToRem(30)}`,
    background: palette.background.default,
    boxShadow: `0 ${typography.pxToRem(5)} ${typography.pxToRem(5)} ${typography.pxToRem(5)} rgba(0, 0, 0, 0.5)`
  },
  img: {
    marginTop: typography.pxToRem(66),
    width: "100%"
  },
  wrapper: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "100vh"
  },
  parcelStatus: {
    fontSize: typography.pxToRem(22),
    lineHeight: typography.pxToRem(34),
    letterSpacing: 0.44,
    color: palette.text.primary,
    margin: "auto"
  },
  logo: {
    padding: `${typography.pxToRem(6)} ${typography.pxToRem(12)}`,
    height: typography.pxToRem(57),
    borderWidth: 1,
    borderColor: palette.border.logoOFD,
    marginRight: typography.pxToRem(22)
  },
  progressContainer: {
    maxWidth: "70%"
  },
  currentStatus: {
    color: palette.text.secondary,
    fontWeight: 300,
    fontSize: typography.pxToRem(16),
    letterSpacing: 0.44
  },
  titleContainer: {
    display: "flex",
    flexDirection: "row",
    marginBottom: typography.pxToRem(16),
    alignSelf: "flex-start"
  },
  logoWithETAContainer: {
    marginBottom: typography.pxToRem(4)
  },
  additionalWrapper: {
    marginBottom: typography.pxToRem(8)
  },
  block: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  idBlock: {
    flexDirection: "row",
    marginLeft: typography.pxToRem(30)
  },
  message: {
    color: palette.text.highlightSuccess,
    fontSize: typography.pxToRem(15),
    fontWeight: "normal"
  },
  idMessage: {
    color: palette.text.primary,
    marginLeft: typography.pxToRem(15),
    textAlign: "center"
  },
  electricMessage: {
    alignSelf: "flex-end"
  },
  identity: {
    height: typography.pxToRem(27),
    width: typography.pxToRem(41)
  },
  plug: {
    marginLeft: typography.pxToRem(12),
    marginRight: typography.pxToRem(36),
    height: typography.pxToRem(41),
    width: typography.pxToRem(75)
  },
  logoWidth: {
    width: typography.pxToRem(75)
  },
  fallbackImage: ({
    footerHeight
  }) => ({
    height: `calc(100% - ${footerHeight}px - ${HEADER_HEIGHT}px)`,
    objectFit: "cover"
  })
}));

const OutForDeliveryDesktopView = ({
  code,
  customerImageLogo,
  statusHtml,
  query,
  electricMessage,
  isVehicleElectric,
  showIdMessage,
  progressData,
  additionalInfo
}) => {
  const bottomComponentRef = React.useRef({});
  const {
    API_HOST
  } = useEnvContext();
  const {
    eventName
  } = useTheme();
  const styles = useStyles({
    footerHeight: bottomComponentRef.current.clientHeight
  });
  const [mapWidth, mapHeight] = useMapSize(bottomComponentRef.current.clientHeight);
  const mapSrc = getMapImage({
    apiHost: API_HOST,
    query,
    code,
    width: mapWidth,
    height: mapHeight,
    themeEvent: eventName
  });
  const deferredMapSrc = useDeferredValue(mapSrc, 200);
  const resultSrc = deferredMapSrc || mapSrc;
  return /*#__PURE__*/React.createElement("div", {
    className: styles.wrapper
  }, resultSrc && /*#__PURE__*/React.createElement(FallBackImage, {
    src: resultSrc,
    className: styles.img,
    fallBackSrc: defaultMap,
    fallBackStyles: styles.fallbackImage,
    alt: "map"
  }), /*#__PURE__*/React.createElement(Grid, {
    container: true,
    direction: "column",
    justify: "space-around",
    alignItems: "center",
    className: styles.container
    /* $FlowFixMe This comment suppresses an error found when upgrading Flow
     * to v0.112.0. To view the error, delete this comment and run Flow. */
    ,
    ref: bottomComponentRef
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.titleContainer
  }, !progressData.showProgressBar && /*#__PURE__*/React.createElement(ParcelLogo, {
    className: styles.logo,
    image: customerImageLogo,
    logoStyles: styles.logoWidth
  }), /*#__PURE__*/React.createElement(Typography, {
    className: styles.parcelStatus,
    dangerouslySetInnerHTML: {
      __html: statusHtml
    }
  })), progressData.showProgressBar && /*#__PURE__*/React.createElement(Grid, {
    container: true,
    direction: "row",
    className: styles.logoWithETAContainer
  }, /*#__PURE__*/React.createElement(Grid, null, /*#__PURE__*/React.createElement(ParcelLogo, {
    className: styles.logo,
    image: customerImageLogo,
    logoStyles: styles.logoWidth
  })), /*#__PURE__*/React.createElement(Grid, {
    container: true,
    direction: "column",
    justify: "space-around",
    className: styles.progressContainer
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true
  }, /*#__PURE__*/React.createElement(TimeProgress, _extends({
    isVehicleElectric: isVehicleElectric
  }, progressData))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    className: styles.bottomLine
  }, /*#__PURE__*/React.createElement(Typography, {
    className: styles.currentStatus
  }, joinStringsWith([progressData.progressStatus, additionalInfo], ". "))))), (isVehicleElectric || showIdMessage) && /*#__PURE__*/React.createElement(Grid, {
    container: true,
    direction: "row",
    justify: "flex-start",
    alignItems: "center",
    className: styles.additionalWrapper
  }, isVehicleElectric && /*#__PURE__*/React.createElement("div", {
    className: styles.block
  }, /*#__PURE__*/React.createElement(Plug, {
    className: styles.plug
  }), /*#__PURE__*/React.createElement(Typography, {
    className: clsx(styles.electricMessage, styles.message)
  }, electricMessage)), showIdMessage && /*#__PURE__*/React.createElement("div", {
    className: clsx(styles.block, styles.idBlock, styles.electricMessage)
  }, /*#__PURE__*/React.createElement(Identity, {
    className: styles.identity
  }), /*#__PURE__*/React.createElement(Typography, {
    className: clsx(styles.message, styles.idMessage)
  }, ID_MESSAGE)))));
};

export default OutForDeliveryDesktopView;